import "./simple-pdf-tool.css";
import "./pdf-password.css";
import { openBrowserPdf, pdfErrorMessage } from "./pdf-browser-core.js";
import { createOperationResult } from "./operation-result.js";
import {
  PDF_PASSWORD_MODES,
  isPasswordProtectedPdfError,
  passwordPdfName,
  passwordWorkerErrorMessage,
  validateProtectPassword,
  validateUnlockPassword
} from "./pdf-password-core.js";

const root = document.querySelector("[data-pdf-password]");

if (root) {
  const mode = root.dataset.mode === PDF_PASSWORD_MODES.UNLOCK ? PDF_PASSWORD_MODES.UNLOCK : PDF_PASSWORD_MODES.PROTECT;
  const ids = ["pdfPasswordStatus", "pdfPasswordUploadPanel", "pdfPasswordDropZone", "choosePasswordPdf", "pdfPasswordFileInput", "pdfPasswordWorkspace", "pdfPasswordFileName", "pdfPasswordFileDetails", "replacePasswordPdf", "pdfPasswordValue", "pdfPasswordConfirmation", "showPdfPasswords", "processPasswordPdf", "pdfPasswordProgress"];
  const elements = Object.fromEntries(ids.map(id => [id, document.getElementById(id)]));
  const state = { file: null, pageCount: 0, busy: false, worker: null };
  const operationResult = createOperationResult(root, elements.pdfPasswordWorkspace);

  function showStatus(message = "") {
    elements.pdfPasswordStatus.textContent = message;
    elements.pdfPasswordStatus.hidden = !message;
  }

  function updateControls() {
    const disabled = state.busy || !state.file;
    elements.replacePasswordPdf.disabled = state.busy;
    elements.pdfPasswordValue.disabled = state.busy;
    if (elements.pdfPasswordConfirmation) elements.pdfPasswordConfirmation.disabled = state.busy;
    elements.showPdfPasswords.disabled = state.busy;
    elements.processPasswordPdf.disabled = disabled;
  }

  function showWorkspace(file, pageCount = 0) {
    state.file = file;
    state.pageCount = pageCount;
    elements.pdfPasswordFileName.textContent = file.name || "document.pdf";
    elements.pdfPasswordFileDetails.textContent = mode === PDF_PASSWORD_MODES.PROTECT
      ? `${pageCount} ${pageCount === 1 ? "page" : "pages"}`
      : "Password-protected PDF";
    elements.pdfPasswordUploadPanel.hidden = true;
    elements.pdfPasswordWorkspace.hidden = false;
  }

  async function loadFile(file) {
    if (state.busy || !file) return;
    showStatus();
    state.busy = true;
    updateControls();
    let loaded = null;
    try {
      if (mode === PDF_PASSWORD_MODES.PROTECT) {
        try {
          loaded = await openBrowserPdf(file);
        } catch (error) {
          if (isPasswordProtectedPdfError(error)) throw new Error("This PDF is already password protected. Unlock it first.");
          throw error;
        }
        showWorkspace(file, loaded.pageCount);
      } else {
        try {
          loaded = await openBrowserPdf(file);
          throw new Error("This PDF is not password protected.");
        } catch (error) {
          if (!isPasswordProtectedPdfError(error)) throw error;
          showWorkspace(file);
        }
      }
      elements.pdfPasswordValue.value = "";
      if (elements.pdfPasswordConfirmation) elements.pdfPasswordConfirmation.value = "";
      elements.pdfPasswordValue.focus();
    } catch (error) {
      showStatus(pdfErrorMessage(error));
    } finally {
      if (loaded?.document) await loaded.document.destroy();
      state.busy = false;
      elements.pdfPasswordFileInput.value = "";
      updateControls();
    }
  }

  function runWorker(file, password) {
    return new Promise(async (resolve, reject) => {
      const worker = new Worker("/dist/pdf-transform-worker.js", { type: "module" });
      state.worker = worker;
      const id = crypto.randomUUID();
      const timeout = window.setTimeout(() => {
        worker.terminate();
        state.worker = null;
        reject({ code: "WORKER_TIMEOUT" });
      }, 120_000);
      const finish = callback => {
        window.clearTimeout(timeout);
        worker.terminate();
        if (state.worker === worker) state.worker = null;
        callback();
      };
      worker.addEventListener("message", event => {
        if (event.data?.id !== id) return;
        if (event.data.ok) finish(() => resolve(event.data.buffer));
        else finish(() => reject({ code: event.data.code || "PROCESS_FAILED" }));
      });
      worker.addEventListener("error", () => finish(() => reject({ code: "WORKER_UNAVAILABLE" })), { once: true });
      try {
        const buffer = await file.arrayBuffer();
        worker.postMessage({ id, mode, password, buffer }, [buffer]);
      } catch (error) {
        finish(() => reject(error));
      }
    });
  }

  async function processPdf() {
    if (state.busy || !state.file) return;
    const password = elements.pdfPasswordValue.value;
    const validation = mode === PDF_PASSWORD_MODES.PROTECT
      ? validateProtectPassword(password, elements.pdfPasswordConfirmation.value)
      : validateUnlockPassword(password);
    if (validation) { showStatus(validation); return; }

    showStatus();
    state.busy = true;
    elements.pdfPasswordProgress.textContent = mode === PDF_PASSWORD_MODES.PROTECT ? "Protecting PDF…" : "Checking password and unlocking…";
    elements.pdfPasswordProgress.hidden = false;
    updateControls();
    let outputDocument = null;
    try {
      elements.pdfPasswordProgress.textContent = mode === PDF_PASSWORD_MODES.PROTECT ? "Applying AES-256 encryption…" : "Removing password protection…";
      const output = await runWorker(state.file, password);
      if (mode === PDF_PASSWORD_MODES.UNLOCK) {
        const outputFile = new File([new Uint8Array(output)], passwordPdfName(state.file.name, mode), { type: "application/pdf" });
        const loaded = await openBrowserPdf(outputFile);
        outputDocument = loaded.document;
        state.pageCount = loaded.pageCount;
        elements.pdfPasswordFileDetails.textContent = `${loaded.pageCount} ${loaded.pageCount === 1 ? "page" : "pages"}`;
      }
      operationResult.show({
        blob: new Blob([new Uint8Array(output)], { type: "application/pdf" }),
        fileName: passwordPdfName(state.file.name, mode),
        message: mode === PDF_PASSWORD_MODES.PROTECT ? "Your PDF was protected successfully." : "Your PDF was unlocked successfully.",
        details: state.pageCount ? [`${state.pageCount} ${state.pageCount === 1 ? "page" : "pages"}`] : []
      });
      elements.pdfPasswordValue.value = "";
      if (elements.pdfPasswordConfirmation) elements.pdfPasswordConfirmation.value = "";
      elements.showPdfPasswords.checked = false;
      root.querySelectorAll('input[type="text"][data-password-input]').forEach(input => { input.type = "password"; });
    } catch (error) {
      showStatus(error?.message || passwordWorkerErrorMessage(mode, error?.code));
    } finally {
      if (outputDocument) await outputDocument.destroy();
      state.busy = false;
      elements.pdfPasswordProgress.hidden = true;
      updateControls();
    }
  }

  elements.choosePasswordPdf.addEventListener("click", event => { event.stopPropagation(); elements.pdfPasswordFileInput.click(); });
  elements.replacePasswordPdf.addEventListener("click", () => elements.pdfPasswordFileInput.click());
  elements.pdfPasswordFileInput.addEventListener("change", () => loadFile(elements.pdfPasswordFileInput.files?.[0]));
  elements.processPasswordPdf.addEventListener("click", processPdf);
  elements.showPdfPasswords.addEventListener("change", () => {
    root.querySelectorAll("[data-password-input]").forEach(input => { input.type = elements.showPdfPasswords.checked ? "text" : "password"; });
  });
  elements.pdfPasswordDropZone.addEventListener("click", event => { if (!event.target.closest("button")) elements.pdfPasswordFileInput.click(); });
  elements.pdfPasswordDropZone.addEventListener("keydown", event => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); elements.pdfPasswordFileInput.click(); } });
  elements.pdfPasswordDropZone.addEventListener("dragover", event => { event.preventDefault(); elements.pdfPasswordDropZone.classList.add("is-dragging"); });
  elements.pdfPasswordDropZone.addEventListener("dragleave", () => elements.pdfPasswordDropZone.classList.remove("is-dragging"));
  elements.pdfPasswordDropZone.addEventListener("drop", event => { event.preventDefault(); elements.pdfPasswordDropZone.classList.remove("is-dragging"); loadFile(event.dataTransfer.files?.[0]); });
  window.addEventListener("beforeunload", () => { operationResult.dispose(); state.worker?.terminate(); });
  updateControls();
}
